import { useState, useContext, useEffect, useMemo } from 'react'
import _ from "lodash"

import { Typography, FormControlLabel, RadioGroup, Radio, Tooltip, Button, Box, Tabs, Tab } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import { PassContext } from '../pass-context'
import { AppContext } from '/providers/app-context'
import ImageSelector from '/components/image-selector'
import dataSelector from './data-selector'
import PopupShell from '../popup-shell'

const instanceDataPaths = {
  "logo": "__images__.__google_logo__",
  "wideLogo": "__images__.__google_wide_logo__"
}

const PopupDialogLogoImage = ({ open, close, sources, logoType, wizard }) => {

  const { classData, objectData, instanceData, scannerInstanceData, setClassData, setObjectData, setInstanceData, setGoogleBoth, passType, openInstanceDataManager } = useContext(PassContext)
  const { imageList } = useContext(AppContext)

  const [newClassData, setNewClassData] = useState({})
  const [newObjectData, setNewObjectData] = useState({})
  const [newInstanceData, setNewInstanceData] = useState({})
  const [dataSource, setDataSource] = useState("template")
  const [imageType, setImageType] = useState("logo")
  const [requiredSize, setRequiredSize] = useState("")

  useEffect(() => {
    if (open) {
      setNewClassData(_.cloneDeep(classData))
    }
  }, [open, classData])

  useEffect(() => {
    if (open) {
      setNewObjectData(_.cloneDeep(objectData))
    }
  }, [open, objectData])

  useEffect(() => {
    if (open) {
      setNewInstanceData(_.cloneDeep(instanceData))
    }
  }, [open, instanceData])

  useEffect(() => {
    if (open) {
      const source = (dataSource === "instance" || sources.source === "object") ? newObjectData : newClassData

      const imageValue = _.get(source, sources[imageType].path)

      if (imageValue) {
        const ds = imageValue.match(/{{.*}}/) ? "instance" : "template"
        setDataSource(ds)
      }
    }
  }, [open, imageType, dataSource, classData, objectData])

  useEffect(() => {
    if (logoType) setImageType(logoType)
  }, [logoType])

  const shortPath = useMemo(() => _.head(sources[imageType].path.split(".")), [imageType])

  const currentImageUrl = useMemo(() => {
    if (!open) return null

    const source = (dataSource === "instance" || sources.source === "object") ? newObjectData : newClassData

    const imageUrl = dataSelector({ objectData: source, instanceData: newInstanceData, scannerInstanceData, path: sources[imageType].path })
    const img = imageList.filter(i => i.s3_url === imageUrl)[0]
    const url = img ? img.s3_url : imageUrl
    return url
  }, [open, sources, dataSource, imageType, newClassData, newObjectData, instanceData])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////


  const handleChangeDataSource = value => { // "instance" or "class/object"

    // Now swap data around to support the new datasource choice
    if (value === "instance") { // template -> instance

      // Remove heroImage from newClassData
      setNewClassData(prev => {
        let nd = { ...prev }
        _.unset(nd, shortPath)
        return nd
      })

      // Ensure reference to instance data goes into newObjectdData
      setNewObjectData(prev => {
        let nd = { ...prev }
        _.set(nd, sources[imageType].path, `{{${instanceDataPaths[imageType].path}}}`)
        return nd
      })

      setNewInstanceData(prev => {
        let nd = { ...prev }
        _.set(nd, instanceDataPaths[imageType].path, currentImageUrl)
        return nd
      })

    } else { // instance -> template

      // Remove instance data
      setNewInstanceData(prev => {
        let nd = { ...prev }
        _.unset(nd, instanceDataPaths[imageType].path
        )
        return nd
      })

      if (sources.source === "object") {
        // Update object data with the actual URL
        setNewObjectData(prev => {
          let nd = { ...prev }
          _.set(nd, sources[imageType].path, currentImageUrl)
          return nd
        })
      } else {
        // Update class data with the actual URL
        setNewClassData(prev => {
          let nd = { ...prev }
          _.set(nd, sources[imageType].path, currentImageUrl)
          return nd
        })

        // Remove object data
        setNewObjectData(prev => {
          let nd = { ...prev }
          _.unset(nd, shortPath)
          return nd
        })
      }
    }

    setDataSource(value)
  }

  const removeImageFromTemplate = () => {
    setNewClassData(prev => {
      let nd = { ...prev }
      _.unset(nd, shortPath)
      return nd
    })

    setNewObjectData(prev => {
      let nd = { ...prev }
      _.unset(nd, shortPath)
      return nd
    })

    setNewInstanceData(prev => {
      let nd = { ...prev }
      _.unset(nd, instanceDataPaths[imageType].path)
      return nd
    })
  }

  const setImageUrl = imageUrl => {
    if (dataSource === "template") {

      if (sources.source === "object") {
        setNewObjectData(prev => {
          let nd = { ...prev }
          _.set(nd, sources[imageType].path, imageUrl)
          return nd
        })
      } else {
        setNewClassData(prev => {
          let nd = { ...prev }
          _.set(nd, sources[imageType].path, imageUrl)
          return nd
        })
      }

    } else {

      setNewInstanceData(prev => {
        let nd = { ...prev }
        _.set(nd, instanceDataPaths[imageType].path, imageUrl)
        return nd
      })

    }
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {

    if (!_.isEqual(newClassData, classData) && !_.isEqual(newObjectData, objectData)) {
      setGoogleBoth({
        googleClass: newClassData,
        googleObject: newObjectData
      })
    } else {
      if (!_.isEqual(newClassData, classData)) setClassData(newClassData)
      if (!_.isEqual(newObjectData, objectData)) setObjectData(newObjectData)
    }

    if (!_.isEqual(newInstanceData, instanceData)) setInstanceData(newInstanceData)
    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose} width={1000} variant="solid">

        <Tabs value={imageType} onChange={(e, v) => setImageType(v)} aria-label="basic tabs example">
          <Tab label="Logo" value="logo" />
          <Tab label="Wide Logo" value="wideLogo" />
        </Tabs>


        {/* Header */}
        <div className="flex-shrink-0 p-2">
          <div className="flex justify-between items-center">
            <Typography color="secondary" variant="h6">{imageType} ({requiredSize})</Typography>

            {!wizard && sources.source === "object" && (
              <RadioGroup row value={dataSource} onChange={e => handleChangeDataSource(e.target.value)}>
                <FormControlLabel value="template" control={<Radio />} label="Same image for all passes" />
                <FormControlLabel value="instance" control={<Radio />} label="Use unique images per pass" />
              </RadioGroup>
            )}

            <Tooltip title="Press ESC to dismiss">
              <Button endIcon={<EastRoundedIcon />} onClick={handleClose}>Cancel</Button>
            </Tooltip>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-5 p-2">
          <ImageSelector
            passType={passType}
            currentImageUrl={currentImageUrl}
            setImageUrl={setImageUrl}
            imageType={imageType}
            deleteAllowed={true}
            removeImageFromTemplate={removeImageFromTemplate}
            setRequiredSize={setRequiredSize}
          />
        </div>

        {/* Footer */}
        <Box className="flex-shrink-0 p-3">
          <hr className="my-2" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openInstanceDataManager} >Edit sample data</Button>
            <Button variant="contained" onClick={saveFormData}>Save</Button>
          </div>
        </Box>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogLogoImage
